import React from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";

const Section = styled.section`
  padding: 64px 0 96px;
  background: var(--nd-black);
`;

const Phase = styled.article`
  border-top: 1px solid var(--nd-border);
  padding: 64px 0;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 48px;
  @media (max-width: 768px) { grid-template-columns: 1fr; gap: 24px; }
`;

const Left = styled.div``;

const Phasenum = styled.div`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 56px;
  font-weight: 700;
  color: var(--nd-text-display);
  line-height: 1;
  margin-bottom: 16px;
`;

const PhaseTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  margin: 0 0 8px;
`;

const Duration = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
`;

const Right = styled.div``;

const Para = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-primary);
  line-height: 1.6;
  margin: 0 0 16px;
`;

const ArtifactsLabel = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 24px 0 12px;
`;

const Artifacts = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Artifact = styled.li`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  color: var(--nd-text-secondary);
  padding: 4px 0;
  &::before { content: '+ '; color: var(--nd-text-display); }
`;

const PHASES = [
  {
    n: "01",
    title: "Audit",
    duration: "Week 1",
    paras: [
      "A senior engineer reads your repos, your terraform, your dashboards, your last three incident postmortems. We get on calls with your team to map who owns what and where the load lives.",
      "You get a one-pager at the end of the week. It has: the gaps we found, the cost leaks we spotted, the three things we'd fix first, and how long each would take. No proposal, no upsell — just the audit.",
      "If you decide not to continue, you keep the audit. We've had clients hand it to a different consultancy.",
    ],
    artifacts: [
      "Infrastructure audit doc (1-pager)",
      "Cost-leak list with rough monthly $ next to each item",
      "Risk register (top 5 incidents waiting to happen)",
      "Recommended first sprint scope",
    ],
  },
  {
    n: "02",
    title: "Build",
    duration: "Months 1–3",
    paras: [
      "We join your Slack, show up to your standups, work in your repos. Most clients forget we're not full-time employees — that's the point. We use your tech stack, your conventions, your ticket queue.",
      "The first sprint comes from the audit: CI/CD that doesn't break, observability you can actually read, IaC that survives a junior touching it, secrets out of plaintext. Quick wins first. The big architecture changes happen once the foundation is steady.",
      "Every PR is reviewed by your team. Every change has a runbook entry. We document as we go so the work outlives the engagement.",
    ],
    artifacts: [
      "Production-grade CI/CD pipeline (GitHub Actions / GitLab / Jenkins)",
      "Observability stack: OpenTelemetry + Prometheus + Grafana or Datadog",
      "Terraform / Pulumi modules with policy guardrails (OPA, Checkov)",
      "Secrets management (Vault, AWS Secrets Manager, SOPS)",
      "Per-service runbooks committed to your docs repo",
    ],
  },
  {
    n: "03",
    title: "Operate",
    duration: "Ongoing",
    paras: [
      "By month three your team owns the stack. We're now the on-call escalation, not the primary. Weekly office hours, monthly architecture reviews, quarterly cost reviews.",
      "Optional: a Dioscuro junior stays embedded long-term at a reduced rate. They've been mentored by the senior who ran the build phase, so the context doesn't walk out the door when we step back.",
      "If something blows up at 3am, we're paged. The runbook your team wrote tells them what to do before we even pick up.",
    ],
    artifacts: [
      "Shared on-call rotation with your team (PagerDuty / Opsgenie)",
      "Monthly executive update (uptime, incidents, costs, next priorities)",
      "Quarterly FinOps review (rightsizing, Savings Plans, Karpenter)",
      "Optional embedded junior with mentor-of-record",
    ],
  },
];

const PhaseDetail = () => (
  <Section>
    <Container>
      {PHASES.map((p) => (
        <Phase key={p.n}>
          <Left>
            <Phasenum>{p.n}</Phasenum>
            <PhaseTitle>{p.title}</PhaseTitle>
            <Duration>{p.duration}</Duration>
          </Left>
          <Right>
            {p.paras.map((para, i) => (
              <Para key={i}>{para}</Para>
            ))}
            <ArtifactsLabel>What you get</ArtifactsLabel>
            <Artifacts>
              {p.artifacts.map((a) => (
                <Artifact key={a}>{a}</Artifact>
              ))}
            </Artifacts>
          </Right>
        </Phase>
      ))}
    </Container>
  </Section>
);
export default PhaseDetail;
